import { memo, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { MdClose } from 'react-icons/md';
import { V2QsMultiTextProps, defaultV2QsMultiTextProps } from './types';

/**
 * V2QsMultiText 컴포넌트
 *
 * 다중 텍스트 태그 입력 컴포넌트
 * V1 QsInputMultiText의 V2 버전으로 순수 UI만 담당
 *
 * 주요 기능:
 * - 입력 필드 + ADD 버튼으로 태그 추가
 * - 태그 목록 (badge 형태)
 * - 태그 삭제 기능
 * - min/max 유효성 검사
 */
function V2QsMultiText(props: V2QsMultiTextProps) {
  const {
    value = [],
    onChange,
    min,
    max,
    disabled = defaultV2QsMultiTextProps.disabled,
    readOnly = defaultV2QsMultiTextProps.readOnly,
    hasError = defaultV2QsMultiTextProps.hasError,
    errorMessage: externalErrorMessage,
    autoFocus,
    placeholder = defaultV2QsMultiTextProps.placeholder,
    addButtonText = defaultV2QsMultiTextProps.addButtonText,
    minErrorMessage = defaultV2QsMultiTextProps.minErrorMessage,
    maxErrorMessage = defaultV2QsMultiTextProps.maxErrorMessage,
    className,
    inputClassName,
    tagClassName,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');

  // 자동 포커스
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // 유효성 검사
  const getValidationError = useCallback((): string | null => {
    if (min !== undefined && value.length < min) {
      return minErrorMessage?.replace('{{min}}', String(min)) || null;
    }
    if (max !== undefined && value.length > max) {
      return maxErrorMessage?.replace('{{max}}', String(max)) || null;
    }
    return null;
  }, [value.length, min, max, minErrorMessage, maxErrorMessage]);

  const validationError = getValidationError();
  const displayError = externalErrorMessage || validationError;
  const showError = hasError || !!validationError;

  // 태그 추가
  const handleAddTag = useCallback(() => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || disabled || readOnly) return;

    // 중복 체크
    if (value.includes(trimmedValue)) {
      setInputValue('');
      return;
    }

    // max 체크
    if (max !== undefined && value.length >= max) {
      return;
    }

    const newTags = [...value, trimmedValue];
    onChange?.(newTags);
    setInputValue('');
  }, [inputValue, value, onChange, disabled, readOnly, max]);

  // 태그 제거
  const handleRemoveTag = useCallback(
    (indexToRemove: number) => {
      if (disabled || readOnly) return;

      const newTags = value.filter((_, index) => index !== indexToRemove);
      onChange?.(newTags);
    },
    [value, onChange, disabled, readOnly]
  );

  // 입력값 변경
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  // Enter 키로 태그 추가
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddTag();
      }
    },
    [handleAddTag]
  );

  // ADD 버튼 활성화 여부
  const isAddButtonEnabled = inputValue.trim() !== '' && !disabled && !readOnly;
  const canAddMore = max === undefined || value.length < max;

  return (
    <div className={classNames('flex flex-col', className)}>
      {/* 에러 메시지 */}
      {showError && displayError && (
        <div className="text-error text-sm opacity-70 p-1 mb-1">{displayError}</div>
      )}

      {/* 입력 필드와 ADD 버튼 */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || !canAddMore}
          readOnly={readOnly}
          className={classNames(
            'input input-bordered w-full rounded-xl pr-20',
            showError && 'input-error',
            inputClassName
          )}
        />

        {/* ADD 버튼 */}
        <button
          type="button"
          onClick={handleAddTag}
          disabled={!isAddButtonEnabled || !canAddMore}
          className={classNames(
            'absolute right-2 top-1/2 transform -translate-y-1/2',
            'btn btn-sm',
            'h-8 min-h-8 rounded-lg px-3'
          )}
          style={{
            backgroundColor: isAddButtonEnabled && canAddMore ? '#3555FC' : '#E5E7EB',
            borderColor: isAddButtonEnabled && canAddMore ? '#3555FC' : '#E5E7EB',
            cursor: isAddButtonEnabled && canAddMore ? 'pointer' : 'not-allowed',
          }}
        >
          <span
            style={{
              color: isAddButtonEnabled && canAddMore ? 'white' : '#9CA3AF',
              fontSize: '12px',
              fontWeight: '600',
            }}
          >
            {addButtonText}
          </span>
        </button>
      </div>

      {/* 태그 목록 */}
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {value.map((tag, index) => (
            <div
              key={`${tag}-${index}`}
              className={classNames(
                'badge badge-lg gap-2 px-3 badge-outline',
                'h-10 min-h-10',
                tagClassName
              )}
              style={{
                backgroundColor: '#fff',
                color: '#3555FC',
                borderColor: '#3555FC',
              }}
            >
              <span>{tag}</span>
              {!readOnly && !disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveTag(index)}
                  className="btn btn-ghost btn-xs btn-circle"
                >
                  <MdClose size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default memo(V2QsMultiText);
